import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tv, ArrowUpRight } from "lucide-react";

// Alphabetized, deduplicated list of representative streaming apps + networks
// where our streaming TV inventory may run. The actual mix depends on the
// targeting categories selected for a given campaign.
const CHANNELS = [
  "A&E",
  "AMC",
  "Animal Planet",
  "BBC America",
  "Bravo",
  "CBS News",
  "CBS Sports Network",
  "CNBC",
  "CNN",
  "Cartoon Network",
  "Comedy Central",
  "DIY Network",
  "Discovery",
  "E! News",
  "ESPN",
  "EllenTube",
  "FOX",
  "FOX Sports",
  "FX",
  "FYI",
  "Food Network",
  "Fox News Channel",
  "Fuse",
  "GSN",
  "Golf Channel",
  "HGTV",
  "Hallmark Channel",
  "History",
  "Investigation Discovery",
  "Lifetime",
  "MTV",
  "NBA TV",
  "NBC",
  "NFL Network",
  "NHL",
  "National Geographic",
  "OWN",
  "Oxygen",
  "Sling TV",
  "Spike",
  "Syfy",
  "TBS",
  "TLC GO",
  "TMZ",
  "TNT",
  "TV Land",
  "Telemundo Now",
  "The CW",
  "Travel Channel",
  "USA",
  "VH1",
  "WE TV",
  "XFINITY Stream",
  "iHeartRadio",
];

const StreamingChannelsModal = ({ triggerLabel, triggerTestId }) => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-[var(--af-lime)] hover:underline underline-offset-4 font-medium"
          data-testid={triggerTestId || "streaming-channels-trigger"}
        >
          <Tv size={12} />
          {triggerLabel || "See where these ads may run"}
          <ArrowUpRight size={12} />
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl bg-[var(--af-navy)] text-[var(--af-cream)] border border-[var(--af-line)] max-h-[85vh] overflow-y-auto"
        data-testid="streaming-channels-modal"
      >
        <DialogHeader>
          <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
            Streaming TV · Sample Inventory
          </div>
          <DialogTitle className="font-display text-3xl sm:text-4xl leading-tight text-[var(--af-cream)]">
            Where your{" "}
            <span className="text-[var(--af-lime)]">streaming ads</span> may
            run.
          </DialogTitle>
          <DialogDescription className="text-sm sm:text-base text-[var(--af-cream)]/75 leading-relaxed pt-3">
            We have direct inventory on <strong>hundreds of apps and
            providers</strong> covering News, Sports, Entertainment, Lifestyle,
            Gaming, Health, Technology, and more — depending on the targeting
            categories selected for your campaign. Below is a sample of the
            apps and providers we can serve on:
          </DialogDescription>
        </DialogHeader>

        <div
          className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm text-[var(--af-cream)]/90"
          data-testid="streaming-channels-list"
        >
          {CHANNELS.map((c) => (
            <div
              key={c}
              className="flex items-center gap-2 py-1.5 border-b border-[var(--af-line)]/50"
              data-testid={`streaming-channel-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <span
                className="w-1 h-1 rounded-full bg-[var(--af-lime)] flex-shrink-0"
                aria-hidden="true"
              />
              {c}
            </div>
          ))}
        </div>

        <p className="text-xs text-[var(--af-cream)]/60 mt-6 leading-relaxed border-t border-[var(--af-line)] pt-4">
          This is a representative sample, not a full media list. The actual
          mix runs on hundreds of apps and providers, curated to your
          audience, category, and campaign goals.
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default StreamingChannelsModal;
